import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { MessageCircle, Image, Video, Trash2, Send, X, Loader2, UserPlus } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import CommentMedia from "@/components/CommentMedia";

// Public comment interface (excludes user_email for privacy)
interface Comment {
  id: string;
  article_id: string;
  user_id: string;
  user_name: string | null;
  content: string | null;
  media_url: string | null;
  media_type: "image" | "video" | null;
  created_at: string;
  updated_at: string;
}

interface ArticleCommentsProps {
  articleId: string;
}

const ArticleComments = ({ articleId }: ArticleCommentsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [content, setContent] = useState("");
  const [postAnonymously, setPostAnonymously] = useState(false);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch comments using the public view (excludes user_email for privacy)
  const { data: comments, isLoading } = useQuery({
    queryKey: ["article-comments", articleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("article_comments_public")
        .select("*")
        .eq("article_id", articleId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Comment[];
    },
  });

  // Subscribe to realtime updates
  useEffect(() => {
    const channel = supabase
      .channel(`comments-${articleId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "article_comments",
          filter: `article_id=eq.${articleId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["article-comments", articleId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [articleId, queryClient]);

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");

    if (!isImage && !isVideo) {
      toast({
        title: "Invalid file type",
        description: "Please select an image or video file.",
        variant: "destructive",
      });
      return;
    }

    // Max 10MB for images, 50MB for videos
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({
        title: "File too large",
        description: `Maximum size is ${isVideo ? "50MB" : "10MB"}.`,
        variant: "destructive",
      });
      return;
    }

    setMediaFile(file);
    setMediaType(isImage ? "image" : "video");
    setMediaPreview(URL.createObjectURL(file));
  };

  const clearMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Submit comment mutation
  const submitComment = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");
      if (!content.trim() && !mediaFile) throw new Error("Comment cannot be empty");

      setIsUploading(true);
      let mediaUrl: string | null = null;

      // Upload media if present
      if (mediaFile) {
        const fileExt = mediaFile.name.split(".").pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("comment-media")
          .upload(fileName, mediaFile);

        if (uploadError) throw uploadError;

        // Store the storage path (not a public URL). Rendering resolves a
        // short-lived signed URL, and the storage RLS policy gates access to
        // approved comments only.
        mediaUrl = fileName;
      }

      // Insert comment
      const { error } = await supabase.from("article_comments").insert({
        article_id: articleId,
        user_id: user.id,
        user_email: user.email!,
        user_name: postAnonymously ? "Anonymous" : (user.user_metadata?.full_name || "Anonymous"),
        content: content.trim() || null,
        media_url: mediaUrl,
        media_type: mediaType,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      setContent("");
      clearMedia();
      toast({
        title: "Comment posted",
        description: "Your comment has been added.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsUploading(false);
    },
  });

  // Delete comment mutation
  const deleteComment = useMutation({
    mutationFn: async (commentId: string) => {
      const { error } = await supabase
        .from("article_comments")
        .delete()
        .eq("id", commentId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Comment deleted",
        description: "Your comment has been removed.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete comment.",
        variant: "destructive",
      });
    },
  });

  const getInitials = (name?: string | null) => {
    if (name) {
      return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
    }
    return "??";
  };

  return (
    <section className="mt-12 pt-8 border-t border-border">
      <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
        <MessageCircle className="h-6 w-6" />
        Comments {comments && comments.length > 0 && `(${comments.length})`}
      </h2>

      {/* Comment Form */}
      {user ? (
        <div className="bg-muted/30 rounded-lg p-4 mb-6">
          <Textarea
            placeholder="Share your thoughts..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="min-h-[100px] mb-3 bg-background"
            disabled={isUploading}
          />

          {/* Media Preview */}
          {mediaPreview && (
            <div className="relative mb-3 inline-block">
              {mediaType === "image" ? (
                <img
                  src={mediaPreview}
                  alt="Preview"
                  className="max-h-40 rounded-lg"
                />
              ) : (
                <video
                  src={mediaPreview}
                  className="max-h-40 rounded-lg"
                  controls
                />
              )}
              <button
                onClick={clearMedia}
                className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1"
                disabled={isUploading}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={isUploading}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <Image className="h-4 w-4 mr-1" />
                  Image
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  <Video className="h-4 w-4 mr-1" />
                  Video
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="anonymous"
                  checked={postAnonymously}
                  onCheckedChange={setPostAnonymously}
                />
                <Label htmlFor="anonymous" className="text-sm text-muted-foreground cursor-pointer">
                  Post anonymously
                </Label>
              </div>
            </div>

            <Button
              onClick={() => submitComment.mutate()}
              disabled={(!content.trim() && !mediaFile) || isUploading}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1" />
              )}
              Post Comment
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-muted/30 rounded-lg p-6 mb-6 text-center">
          <UserPlus className="h-8 w-8 mx-auto mb-3 text-primary" />
          <h3 className="font-semibold text-foreground mb-1">Join the conversation</h3>
          <p className="text-muted-foreground mb-4 text-sm">
            Create a free account to leave comments, share photos, and connect with other gardeners.
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/auth" rel="nofollow" state={{ from: typeof window !== "undefined" ? window.location.pathname : "" } as any}>
              <Button>Sign Up Free</Button>
            </Link>
            <Link to="/auth" rel="nofollow" state={{ from: typeof window !== "undefined" ? window.location.pathname : "" } as any}>
              <Button variant="outline">Sign In</Button>
            </Link>
          </div>
        </div>
      )}

      {/* Comments List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1">
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-16 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : comments && comments.length > 0 ? (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="flex gap-3 p-4 bg-card rounded-lg border border-border"
            >
              <Avatar>
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {getInitials(comment.user_name)}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">
                      {comment.user_name || "Anonymous"}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(comment.created_at), "MMM d, yyyy 'at' h:mm a")}
                    </span>
                  </div>

                  {user?.id === comment.user_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteComment.mutate(comment.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {comment.content && (
                  <p className="text-foreground/80 whitespace-pre-wrap break-words">
                    {comment.content}
                  </p>
                )}

                {comment.media_url && (
                  <div className="mt-3">
                    <CommentMedia path={comment.media_url} type={comment.media_type} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-muted-foreground py-8">
          No comments yet. Be the first to share your thoughts!
        </p>
      )}
    </section>
  );
};

export default ArticleComments;
